import { createUplaneApi } from './api';
import { findTrackerScript, isDebugMode, readScriptOptions } from './config';
import {
  applyFramerConsentToOptions,
  getFramerConsentStorage,
  installFramerConsentAdapter,
  isFramerWebsite,
  readFramerAnalyticsConsent
} from './consent/framer';
import type { UplaneApi } from './contracts';
import { captureInboundLink } from './linker';
import { createLogger } from './logger';

declare global {
  interface Window {
    Uplane: UplaneApi;
    UplaneReady: Promise<UplaneApi>;
  }

  interface WindowEventMap {
    'uplane:ready': CustomEvent<UplaneApi>;
  }
}

export const Uplane = createUplaneApi();

const initializeFromScript = async (script: HTMLScriptElement | null): Promise<UplaneApi> => {
  if (!script || script.dataset.autostart === 'false') return Uplane;

  const options = readScriptOptions(script);
  if (!options) {
    createLogger(isDebugMode()).error('read_config', {
      outcome: 'failed',
      error: 'data-workspace-id and data-region="US|EU" are required'
    });
    return Uplane;
  }

  const logger = createLogger(isDebugMode(), options.workspaceId);
  const useFramerConsent = isFramerWebsite(document)
    && (options.policyBasis ?? 'opt_in') === 'opt_in';
  const framerStorage = useFramerConsent ? getFramerConsentStorage(logger) : null;
  const initialFramerConsent = useFramerConsent
    ? readFramerAnalyticsConsent(framerStorage)
    : null;
  const initialOptions = initialFramerConsent
    ? applyFramerConsentToOptions(options, initialFramerConsent)
    : options;

  try {
    await Uplane.init(initialOptions);
    if (initialFramerConsent) {
      installFramerConsentAdapter({
        api: Uplane,
        initialConsent: initialFramerConsent,
        logger,
        storage: framerStorage
      });
    }
  } catch (error: unknown) {
    logger.error('autostart', {
      outcome: 'failed',
      error: error instanceof Error ? error.message : String(error)
    });
  }

  return Uplane;
};

if (typeof window !== 'undefined' && typeof document !== 'undefined') {
  captureInboundLink(createLogger(isDebugMode()));
  const script = findTrackerScript();
  window.Uplane = Uplane;
  window.UplaneReady = initializeFromScript(script);
  void window.UplaneReady.then((api) => {
    window.dispatchEvent(new CustomEvent('uplane:ready', { detail: api }));
  });
}

export { createUplaneApi } from './api';
export type {
  CustomEventProperties,
  TrackerInitOptions,
  TrackingPolicyBasis,
  TrackingPrivacyMode,
  TrackingRegion,
  UplaneApi
} from './contracts';
